import { z } from "zod";

/* ===========================================================================
 * US-E19.F3-0202-S02 (@UC9303) — PHÂN QUYỀN HIỂN THỊ (redaction) + scope view.
 *
 * SHAPE THUẦN — contract KHÔNG chứa logic quyết định show/redact (thuộc domain
 * `resolveVisibility` — S03). Ở đây chỉ định hình "có số" vs "đã ẩn".
 *
 * 🔴 KHÔNG BAO GIỜ tồn tại trạng thái mâu thuẫn (số thật + redacted:true): dùng DISCRIMINATED UNION
 * theo `redacted` ⇒ nhánh redacted BẮT BUỘC `amountMinor` = literal '•••' (z.literal) — schema tự
 * ném nếu service lỡ serialize số thật kèm redacted:true (fail-loud, không rò tiền lặng lẽ).
 *
 * Tiền LUÔN là STRING minor-unit (B1 BigInt, no-float). Ẩn tiền ⇒ placeholder, KHÔNG '0'
 * (0 là NÓI DỐI về tiền).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/** Placeholder tiền/nội dung ĐÃ ẨN. 1 NGUỒN SỰ THẬT — api + FE import từ đây (không hardcode). */
export const REDACTED_PLACEHOLDER = "•••" as const;

/** Mức hiển thị per-item (khớp CHECK expenses_visibility_check / debt_entries_visibility_check). */
export const VISIBILITY = {
  PUBLIC: "public",
  PRIVATE: "private",
  SENSITIVE: "sensitive",
} as const;
export type Visibility = (typeof VISIBILITY)[keyof typeof VISIBILITY];

export const visibilitySchema = z.enum([
  VISIBILITY.PUBLIC,
  VISIBILITY.PRIVATE,
  VISIBILITY.SENSITIVE,
]);

/** Kết quả resolver hiển thị (domain S03) — 2 giá trị, fail-closed mặc định 'redact'. */
export const DISPLAY_SCOPE = {
  SHOW: "show",
  REDACT: "redact",
} as const;
export type DisplayScope = (typeof DISPLAY_SCOPE)[keyof typeof DISPLAY_SCOPE];
export const displayScopeSchema = z.enum([DISPLAY_SCOPE.SHOW, DISPLAY_SCOPE.REDACT]);

/** Field chung mọi item (không nhạy cảm): id + mức hiển thị + mốc tạo. */
const baseItemShape = {
  id: z.string().uuid(),
  visibility: visibilitySchema,
  createdAt: z.string().datetime(),
} as const;

/** Nhánh SHOW: số THẬT (string BigInt minor) + note thật (nullable). */
const shownExpenseItemSchema = z
  .object({
    ...baseItemShape,
    redacted: z.literal(false),
    name: z.string(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    note: z.string().nullable(),
    status: z.string(),
  })
  .strict();

/**
 * Nhánh REDACT: KHÔNG có số thật (literal '•••'), note ẩn. `name`/`status` giữ (không phải tiền) để
 * FE render "có khoản chi này nhưng bạn không được xem số" — minh bạch SỰ TỒN TẠI, ẩn CON SỐ.
 */
const redactedExpenseItemSchema = z
  .object({
    ...baseItemShape,
    redacted: z.literal(true),
    name: z.string(),
    amountMinor: z.literal(REDACTED_PLACEHOLDER),
    currency: z.string().length(3),
    note: z.literal(REDACTED_PLACEHOLDER).nullable(),
    status: z.string(),
  })
  .strict();

export const expenseScopedItemSchema = z.discriminatedUnion("redacted", [
  shownExpenseItemSchema,
  redactedExpenseItemSchema,
]);
export type ExpenseScopedItem = z.infer<typeof expenseScopedItemSchema>;

const shownDebtItemSchema = z
  .object({
    ...baseItemShape,
    redacted: z.literal(false),
    kind: z.string(),
    debtorMemberId: z.string().uuid(),
    creditorMemberId: z.string().uuid().nullable(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
  })
  .strict();

/** Nợ bị ẩn: nhóm CHỈ biết "có khoản riêng" — KHÔNG số, KHÔNG danh tính 2 bên. */
const redactedDebtItemSchema = z
  .object({
    ...baseItemShape,
    redacted: z.literal(true),
    kind: z.string(),
    debtorMemberId: z.null(),
    creditorMemberId: z.null(),
    amountMinor: z.literal(REDACTED_PLACEHOLDER),
    currency: z.string().length(3),
  })
  .strict();

export const debtScopedItemSchema = z.discriminatedUnion("redacted", [
  shownDebtItemSchema,
  redactedDebtItemSchema,
]);
export type DebtScopedItem = z.infer<typeof debtScopedItemSchema>;

/**
 * audit P-H-6 (wave4): phân trang keyset cho scoped-ledger — envelope thêm `pageInfo` (ADDITIVE).
 * expenses + debts là MỘT dòng thời gian gộp (createdAt desc, id desc) phân trang CHUNG bằng 1
 * cursor opaque (mirror common/pagination/cursor). `hasNext`/`nextCursor` khớp quy ước list repo.
 */
export const scopedLedgerPageInfoSchema = z
  .object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type ScopedLedgerPageInfo = z.infer<typeof scopedLedgerPageInfoSchema>;

/**
 * Response GET /v1/funds/:id/scoped-ledger — view end-user CÓ REDACTION.
 * `totalVisibleMinor` = Σ số tiền của các item viewer ĐƯỢC XEM (PRIV-VIEW-SCOPE: member thường vẫn
 * thấy TỔNG minh bạch của phần được xem, chi tiết nhạy cảm bị ẩn). String BigInt minor.
 *
 * 🔴 P-H-6: `redactedCount` + `totalVisibleMinor` là số TOÀN-QUỸ (KHÔNG đổi theo trang — semantics
 * giữ nguyên trước khi thêm phân trang); `expenses`/`debts` là item CỦA TRANG hiện tại.
 */
export const scopedLedgerResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    currency: z.string().length(3),
    viewerRole: z.string(),
    viewerLeftAt: z.string().datetime().nullable(),
    expenses: z.array(expenseScopedItemSchema),
    debts: z.array(debtScopedItemSchema),
    redactedCount: z.number().int().nonnegative(),
    totalVisibleMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    pageInfo: scopedLedgerPageInfoSchema,
  })
  .strict();
export type ScopedLedgerResponse = z.infer<typeof scopedLedgerResponseSchema>;
